<template>
  <div>
    <div id="billing-dataview" class="page-table scrollable">
      <div class="flex align-center mt-5 mb-10">
        <el-input
          placeholder="Search name"
          v-model="accountSearch"
          class="mr-10"
          style="max-width: 193px;"
          clearable
        />
        <el-select
          v-model="monthSelection"
          placeholder="Select Billing Month"
          filterable
        >
          <el-option
            v-for="(month, index) in months"
            :key="index"
            :label="month.label"
            :value="month.value"
          />
        </el-select>
        <el-button class="mb-0" @click="doExport" style="margin-left: auto;">
          Export
        </el-button>
        <el-button @click="doExportCDR" v-if="hasCDRExport">Export CDR</el-button>
      </div>

      <div class="billing-table card-base card-shadow--medium">
        <vxe-grid
          round
          class="user-stats-table"
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 6, oSize: 0 }"
          :loading="loading || tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ExportToCsv } from "export-to-csv";
import moment from "moment-timezone";
import { debounce } from "lodash";
import feathersClient from "../../../../../feathers/feathers-client";

export default {
  name: "BillingRecords",
  props: {
    records: { required: true },
    loading: { required: true }
  },
  components: {},
  data() {
    return {
      hasCDRExport: false,
      cdrUrl: null,
      exportCSV: false,
      search: "",
      total: 0,
      billingsChannel: null,
      tableData: [],
      allData: [],
      visibleActionsId: "",
      tableLoading: false,
      monthSelection: null,
      startDate: null,
      endDate: null,
      months: [],
      accountSearch: "",
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-blue" sprite="list-numbers" />
                </div>
              ];
            }
          }
        },
        {
          field: "account",
          title: "Account",
          sortable: true,
          minWidth: 250,
          showOverflow: true
        },
        {
          field: "billingId",
          title: "Billing Code",
          sortable: true,
          sortType: "number",
          minWidth: 175,
          showOverflow: true
        },
        {
          field: "extensions",
          title: "Extensions",
          sortable: true,
          minWidth: 150,
          showOverflow: false,
          sortType: "number"
        },
        {
          field: "faxes",
          title: "Faxes",
          sortable: true,
          minWidth: 150,
          showOverflow: false,
          sortType: "number"
        },
        {
          field: "sipTrunks",
          title: "Sip Trunks",
          sortable: true,
          minWidth: 150,
          showOverflow: false,
          sortType: "number"
        },
        {
          field: "phoneNumbers",
          title: "Phone Numbers",
          sortType: "number",  
          sortable: true,
          minWidth: 150,
          showOverflow: false
        },
        {
          field: "interstateIn",
          title: "Interstate In",
          sortable: true,
          minWidth: 150,
          showOverflow: true,
          sortType: "number"
        },
        {
          field: "interstateOut",
          title: "Interstate Out",
          sortable: true,
          minWidth: 150,
          showOverflow: true,
          sortType: "number"
        },
        {
          field: "intrastateIn",
          title: "Intrastate In",
          sortable: true,
          minWidth: 150,
          showOverflow: true,
          sortType: "number"
        },
        {
          field: "intrastateOut",
          title: "Intrastate Out",
          sortable: true,
          minWidth: 150,
          showOverflow: true,
          sortType: "number"
        },

        {
          field: "tollFreeMinutes",
          title: "Toll Free Mins",
          sortable: true,
          minWidth: 150,
          showOverflow: false,
          sortType: "number"
        },
        {
          field: "interstateInMinutes",
          title: "Interstate In Minutes",
          sortable: true,
          minWidth: 200,
          showOverflow: false,
          sortType: "number"
        },
        {
          field: "interstateOutMinutes",
          title: "Interstate Out Minutes",
          sortable: true,
          minWidth: 200,
          showOverflow: false,
          sortType: "number"
        },
        {
          field: "intrastateInMinutes",
          title: "Intrastate In Minutes",
          sortable: true,
          minWidth: 200,
          showOverflow: false,
          sortType: "number"
        },
        {
          field: "intrastateOutMinutes",
          title: "Intrastate Out Minutes",
          sortable: true,
          minWidth: 200,
          showOverflow: false,
          sortType: "number"
        },
        {
          field: "internationalMinutes",
          title: "International Minutes",
          sortable: true,
          minWidth: 200,
          showOverflow: false,
          sortType: "number"
        }
      ]
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    records() {
      if (this.records) {
        this.accountSearch = "";
        this.setMonths();
        this.tableData = [];
        this.total = this.tableData.length;
      }
    },

    monthSelection(val) {
      this.onMonthSelection();
      this.total = this.tableData.length;
    },

    accountSearch: debounce(function() {
      this.filterAccounts();
    }, 300)
  },

  methods: {

    downloadCSV(data) {
      const { url, downloadName } = data;
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadName;
      link.click();
    },

    doExportCDR() {
      this.downloadCSV({url: this.cdrUrl, downloadName: `cdr_${this.monthSelection}`});
    },

    doExport() {
      this.$confirm(`Export data for ${this.total} accounts?`, {
        type: "warning"
      })
        .then(() => {
          //export from browser
          let csvData = [];
          for (let i = 0; i < this.tableData.length; i++) {
            const keys = [
              "account",
              "billingId",
              "extensions",
              "faxes",
              "sipTrunks",
              "phoneNumbers",
              "interstateIn",
              "intrastateIn",
              "interstateOut",
              "intrastateOut",
              "tollFreeMinutes",
              "interstateInMinutes",
              "intrastateInMinutes",
              "interstateOutMinutes",
              "intrastateOutMinutes",
              "internationalMinutes"
            ];

            const csvObject = {};

            for (let key of keys) {
              csvObject[key] =
                this.tableData[i][key] === null ||
                this.tableData[i][key] === undefined
                  ? 0
                  : this.tableData[i][key];
            }

            csvData.push(csvObject);
          }

          const options = {
            filename: "billing_summary",
            showLabels: true,
            headers: [
              "Account",
              "Billing Code",
              "Extensions",
              "Faxes",
              "SIP Trunks",
              "Phone Numbers",
              "Inter State In",
              "Intra State In",
              "Inter State Out",
              "Intra State Out",
              "Toll Free Minutes",
              "Inter State In Mins.",
              "Intra State In Mins.",
              "Inter State Out Mins.",
              "Intra State Out Mins.",
              "International Mins."
            ]
          };

          const csvExporter = new ExportToCsv(options);
          csvExporter.generateCsv(csvData);
        })
        .catch(() => true);
    },
    //General Form and Table Operations
    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },
    onMonthSelection() {
      this.tableLoading = true;

      // reverse records
      const newRecords = this.records.slice().reverse();
      const billingDetailRecords = newRecords[this.monthSelection].resellerData.accounts;
      const billingData = newRecords[this.monthSelection].data;

      //check if there is a billing cdr file available for selected month
      if(billingData) {
        this.hasCDRExport = !!billingData.cdrUrl;
        this.cdrUrl = billingData.cdrUrl;
      }

      this.tableData = [...billingDetailRecords];
      this.allData = [...billingDetailRecords];
      this.tableLoading = false;
      this.accountSearch = "";
    },

    setMonths() {
      const lastIndex = this.records.length - 1;
      this.months = this.records.map((item, index) => ({
        label: moment(item.date).format("MMMM - YYYY"),
        value: lastIndex - index,
        exactValue: item.date
      }));
      if (this.months.length) {
        this.monthSelection = this.months[0].value;
      }
    },

    filterAccounts() {
      this.tableData = this.accountSearch
        ? this.allData.filter(item =>
            item.account
              .toLowerCase()
              .includes(this.accountSearch.toLowerCase())
          )
        : (this.tableData = [...this.allData]);
    }
  },

  filters: {},

};
</script>

<style lang="scss" scoped>
.billing-table {
  height: 450px;
}
</style>
